"use client";

import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Stethoscope, Clock, ShieldAlert, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const treatmentAreas = [{
  id: 1,
  title: "Metabolic Optimization",
  description: "Enhance your body's natural efficiency in processing nutrients and energy."
}, {
  id: 2,
  title: "Insulin Sensitivity",
  description: "Improve glucose regulation to support stable energy and weight control."
}, {
  id: 3,
  title: "Appetite Regulation",
  description: "Clinically manage hunger signals to facilitate sustainable dietary changes."
}, {
  id: 4,
  title: "Lean Muscle Retention",
  description: "Ensure weight loss comes from fat stores while preserving vital muscle mass."
}, {
  id: 5,
  title: "Lipid Profile Health",
  description: "Support healthier cholesterol levels through targeted metabolic intervention."
}, {
  id: 6,
  title: "Energy Level Support",
  description: "Avoid the fatigue often associated with weight loss via clinical oversight."
}, {
  id: 7,
  title: "Cortisol Management",
  description: "Address stress-related weight gain by balancing key metabolic hormones."
}, {
  id: 8,
  title: "Long-term Maintenance",
  description: "Establish the physiological foundation for keeping weight off permanently."
}];
const expectations = [{
  id: 1,
  title: "Clinical Metabolic Screening",
  description: "Your journey starts with a comprehensive laboratory assessment and physical evaluation to understand your unique metabolic profile.",
  icon: Stethoscope
}, {
  id: 2,
  title: "Precision Protocol Design",
  description: "We develop a medically-supervised plan tailored to your results, incorporating nutrition, lifestyle, and pharmacological support if needed.",
  icon: Clock
}, {
  id: 3,
  title: "Ongoing Medical Oversight",
  description: "Regular check-ins ensure your safety and progress. We adjust your protocol in real-time based on your body's specific response.",
  icon: ShieldAlert
}, {
  id: 4,
  title: "Sustainable Transformation",
  description: "As you reach your goals, we transition you to a maintenance phase designed to solidify your new metabolic set point for life.",
  icon: Sparkles
}];
export default function TreatmentAreasWhatToExpect() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const leftColumnReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const rightColumnReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return;
}